import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';
import KitchenMessagesEditor from './KitchenMessagesEditor';
import CounterDepositsSettings from '../deposits/CounterDepositsSettings';
import { LoadFailed, SavedNote, SwitchRow, useSavedFlash } from './instantSettings';
import { patchSettings } from './patchSettings';
import { SettingsSheet, SheetBlock, SheetSection } from './sheet';
import { StoreLoadState } from './storeSettingsForm';

interface DineInSettingsData {
  id: number;
  store_id: number;
  course_management_enabled: boolean;
  // auto_print_kitchen_ticket: από 14/10/2026 στους «Εκτυπωτές» → «Τι πάει πού» (διακόπτης «Τραπέζια»).
  print_correction_documents: boolean;
  guest_count_enabled?: boolean;
  bill_print_enabled?: boolean;
  kitchen_messages_enabled?: boolean;
  kitchen_messages?: string[];
  // Χωρίς διακόπτη (πάντα ενεργά) από 6/10/2026: επιλογές & «χωρίς» στο PDA, «Επί πιστώσει»,
  // ακύρωση λάθος πληρωμής· από 12/10/2026 και η αναφορά κερασμάτων. Καταργήθηκαν ο «καθαρισμός»
  // (το τραπέζι ελευθερώνεται αμέσως) και το «Ζήτησε λογαριασμό». Ο server δεν τα στέλνει πια.
}

// «Ρυθμίσεις Σάλας», διάταξη «Φύλλο» (13/10/2026): Κουζίνα | Τραπέζια. Κάθε αλλαγή αποθηκεύεται αμέσως.
const KITCHEN = 'dinein-kitchen';
const TABLES = 'dinein-tables';
const TABLE_KEYS: Array<keyof DineInSettingsData> = ['guest_count_enabled', 'bill_print_enabled'];

const DineInSettings: React.FC = () => {
  const { t } = useTranslation(['settings']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const [settings, setSettings] = useState<DineInSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedSection, flashSaved] = useSavedFlash();

  useEffect(() => {
    if (store?.id) {
      fetchSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store?.id}/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch dine-in settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async <K extends keyof DineInSettingsData>(key: K, value: DineInSettingsData[K]) => {
    if (!settings) return;
    const previous = settings[key];

    // Optimistic update — σε αποτυχία (4xx/5xx/δίκτυο) γυρίζει πίσω ΜΟΝΟ αυτό το πεδίο και λέει γιατί,
    // όπως Take Away και Delivery (ως 13/10 ξαναφόρτωνε σιωπηλά· χωρίς δίκτυο έμενε η λάθος τιμή).
    setSettings(s => (s ? { ...s, [key]: value } : s));

    const result = await patchSettings<DineInSettingsData>(
      `${API_BASE_URL}/api/dine-in/${store?.id}/settings`, { [key]: value }
    );
    if (result.ok) {
      setSettings(result.data);
      flashSaved(TABLE_KEYS.includes(key) ? TABLES : KITCHEN);
    } else {
      setSettings(s => (s ? { ...s, [key]: previous } : s));
      showToast('error', t(result.status === 403 ? 'settings:dineIn.adminOnly' : 'settings:dineIn.saveFailed'));
    }
  };

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.operations')} title={t('settings:dineIn.title')} subtitle={t('settings:dineIn.subtitle')}>
      {loading ? (
        <SheetBlock><StoreLoadState status="loading" onRetry={fetchSettings} /></SheetBlock>
      ) : !settings ? (
        <SheetBlock><LoadFailed message={t('settings:dineIn.loadFailed')} onRetry={fetchSettings} /></SheetBlock>
      ) : (
        <>
          <SheetSection id={KITCHEN} title={t('settings:dineIn.kitchen')} hint={t('settings:dineIn.kitchenHint')}
            aside={<SavedNote show={savedSection === KITCHEN} />}>
            {/* Το kds_enabled δεν έχει διακόπτη εδώ (ήταν διακόπτης-βιτρίνα που μπέρδευε): το διαβάζει
                μόνο η λίστα ρόλων υπαλλήλων, για να δείξει τον ρόλο «κουζίνα». */}
            <SwitchRow
              title={t('settings:dineIn.correctionsLabel')}
              subtitle={t('settings:dineIn.correctionsDesc')}
              note={t('settings:dineIn.correctionsNote')}
              checked={settings.print_correction_documents}
              onChange={(value) => updateSetting('print_correction_documents', value)}
            />
            <SwitchRow
              title={t('settings:dineIn.courseLabel')}
              subtitle={t('settings:dineIn.courseDesc')}
              checked={settings.course_management_enabled}
              onChange={(value) => updateSetting('course_management_enabled', value)}
            />
            {/* Κλειστό από προεπιλογή (migration 246). Πύλη και στο backend: το κουμπί → 403
                feature_disabled, τα μηνύματα μιας παραγγελίας αγνοούνται. */}
            <SwitchRow
              title={t('settings:dineIn.kitchenMessagesLabel')}
              subtitle={t('settings:dineIn.kitchenMessagesDesc')}
              note={t('settings:dineIn.kitchenMessagesNote')}
              checked={settings.kitchen_messages_enabled === true}
              onChange={(value) => updateSetting('kitchen_messages_enabled', value)}
            />
            {settings.kitchen_messages_enabled === true && (
              <KitchenMessagesEditor
                messages={settings.kitchen_messages || []}
                onChange={(next) => updateSetting('kitchen_messages', next)}
              />
            )}
          </SheetSection>

          <SheetSection id={TABLES} title={t('settings:dineIn.tables')} hint={t('settings:dineIn.tablesHint')}
            aside={<SavedNote show={savedSection === TABLES} />}>
            {/* Κλειστό από προεπιλογή: άτομα και θέσεις έχουν νόημα μόνο μαζί με
                σύστημα κρατήσεων. Η πύλη υπάρχει και στο backend (guest_count = 1). */}
            <SwitchRow
              title={t('settings:dineIn.guestCountLabel')}
              subtitle={t('settings:dineIn.guestCountDesc')}
              note={t('settings:dineIn.guestCountNote')}
              checked={settings.guest_count_enabled === true}
              onChange={(value) => updateSetting('guest_count_enabled', value)}
            />
            {/* Κλειστό από προεπιλογή: χαρτί προς τον πελάτη πριν το φορολογικό στοιχείο.
                Πύλη και στο backend (403 feature_disabled). */}
            <SwitchRow
              title={t('settings:dineIn.billPrintLabel')}
              subtitle={t('settings:dineIn.billPrintDesc')}
              note={t('settings:dineIn.billPrintNote')}
              checked={settings.bill_print_enabled === true}
              onChange={(value) => updateSetting('bill_print_enabled', value)}
            />
          </SheetSection>

          {/* Προκαταβολές στο ταμείο (12/10/2026, κλάδος feat/pos-deposits): κλειστό από προεπιλογή, πύλη
              και στο backend (403 feature_disabled· στην παραγωγή 409 counter_deposits_not_ready ως τη Φάση 2).
              Αποθηκεύεται στις ρυθμίσεις κρατήσεων· εδώ μόνο ως ενότητα του «Φύλλου». */}
          {store?.id && (
            <SheetSection id="dinein-deposits" title="Προκαταβολές στο ταμείο">
              <CounterDepositsSettings storeId={store.id} />
            </SheetSection>
          )}
        </>
      )}
    </SettingsSheet>
  );
};

export default DineInSettings;
